import { useState, useEffect, useRef, useContext, useMemo } from "react";
import { format, getDay } from "date-fns";
import { de } from "date-fns/locale";
import DateChooser from "../../components/Booking/BookingPhone/DateChooser";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
// import { getLowestPackagePrice } from '../../util/getLowestPackagePrice';
import { useBooking } from "../../store/bookingStore";
import classes from "./Booking.module.css";

import { useBookingPricingSync } from '../../hooks/useBookingPricingSync';
import { getWeekendSurchargeLineLabel } from '../../util/getWeekendSurchargeLineLabel';
import { getDiscountLineLabel } from '../../util/getDiscountLineLabel';
import { parseLocalDateString } from '../../util/parseLocalDate';
import { resolvePackagePriceEntry } from '../../util/resolvePackagePriceEntry';
import { calculatePreExtrasTotal } from '../../util/calculatePreExtrasTotal';
import {
  formatLocalDate,
  getAvailabilityRangesForDate,
  getBookingDurationHours,
  getBusyIntervalItems,
  hasBookableTimeSlot,
  isBookingRangeAvailable,
} from '../../util/studioAvailability';
import { NavBarContext } from '../../App';
import { SmallModal } from "../../components/UI/SmallModal/SmallModal";
import { storeRedirectPathTo } from "../../util/redirectUtils";

export const Booking = ({ setDate, startTime, setStartTime, endTime,
                          setEndTime, numberOfPeople, setNumberOfPeople,
                          setShowBookingProcedure,
                          studioPrice,
                          minHours,
                          bookingStudioData,
                          userId,
                          studioUserId,
                          studioCreativesData,
                          pricingMode,
                          packages,
                          showPackageModal,
                          setShowPackageModal,
                          packageModalFromOverlay,
                          setPackageModalFromOverlay,
                        }) => {

  const [times, setTimes] = useState([]);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showPeopleDropdownMain, setShowPeopleDropdownMain] = useState(false);
  const [showPeopleDropdownModal, setShowPeopleDropdownModal] = useState(false);
  const [timeValidationError, setTimeValidationError] = useState(false);
  const datePickerRef = useRef(null);
  const peopleDropdownMainRef = useRef(null);
  const peopleDropdownModalRef = useRef(null);
  const packageDropdownMainRef = useRef(null);
  const packageDropdownModalRef = useRef(null);
  const { setShowSignIn, isPhoneSize } = useContext(NavBarContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user: authenticatedUser } = useSelector((state) => state?.auth || {});
  const authenticatedUserId = authenticatedUser?.id || authenticatedUser?._id;
  const hasAuthenticatedUser = Boolean(isAuthenticated && authenticatedUserId);
  const isCurrentUserStudioOwner = Boolean(
    hasAuthenticatedUser && studioUserId && authenticatedUserId === studioUserId
  );

  const { bookingData, updateBookingData } = useBooking();

  const [addFreelancer, setAddFreelancer] = useState(bookingData?.addFreelancer || false);

  useEffect(() => {
    updateBookingData('addFreelancer', addFreelancer);
  }, [addFreelancer]);

  // const lowestPackagePrice = useMemo(() => getLowestPackagePrice(studioCreativesData), [studioCreativesData]);

  // ---- Pricing mode helpers (studio packages vs hourly) ----
  // IMPORTANT:
  // - Only treat as package pricing when pricingMode is explicitly "PACKAGES"
  // - Studios with pricingMode === "HOURLY" must stay hourly even if packages exist
  const isPackagePricing = useMemo(
    () => pricingMode === 'PACKAGES',
    [pricingMode]
  );

  // Ensure that stale package fields are cleared for HOURLY studios so
  // downstream flows (Checkout, backend payload) don't misinterpret
  // an hourly booking as a package booking.
  useEffect(() => {
    if (!isPackagePricing) {
      if (
        bookingData.packageId ||
        bookingData.packageName ||
        bookingData.packageDurationHours ||
        bookingData.selectedPeopleGroupKey
      ) {
        updateBookingData('packageId', '');
        updateBookingData('packageName', '');
        updateBookingData('packageDurationHours', null);
        updateBookingData('selectedPeopleGroupKey', null);
      }
    }
  }, [
    isPackagePricing,
    bookingData.packageId,
    bookingData.packageName,
    bookingData.packageDurationHours,
    bookingData.selectedPeopleGroupKey,
    updateBookingData,
  ]);

  const normalizedPackages = useMemo(() => {
    if (!isPackagePricing) return [];
    return [...packages].sort((a, b) => {
      const aHours = a?.durationHours ?? 0;
      const bHours = b?.durationHours ?? 0;
      return aHours - bHours;
    });
  }, [isPackagePricing, packages]);

  const pricingBookingStudioData = useMemo(() => {
    if (!isPackagePricing) return bookingStudioData;
    if (!bookingStudioData && normalizedPackages.length === 0) return null;

    return {
      ...(bookingStudioData || {}),
      pricingMode: 'PACKAGES',
      packages: normalizedPackages.length > 0
        ? normalizedPackages
        : (Array.isArray(bookingStudioData?.packages) ? bookingStudioData.packages : []),
    };
  }, [bookingStudioData, isPackagePricing, normalizedPackages]);

  useBookingPricingSync({
    bookingStudioData: pricingBookingStudioData,
    bookingData,
    updateBookingData,
    studioCreativesData,
    isPackagePricing,
  });

  // Unique person groups across all packages
  const personGroups = useMemo(() => {
    if (!isPackagePricing) return [];
    const map = new Map();
    normalizedPackages.forEach((pkg) => {
      if (!pkg?.prices || !Array.isArray(pkg.prices)) return;
      pkg.prices.forEach((p) => {
        if (!p) return;
        const min = typeof p.minPersons === 'number' ? p.minPersons : parseInt(p.minPersons || '0', 10);
        const max = typeof p.maxPersons === 'number' ? p.maxPersons : parseInt(p.maxPersons || '0', 10);
        if (!min || !max) return;
        const key = `${min}-${max}`;
        if (!map.has(key)) {
          map.set(key, { min, max, key });
        }
      });
    });
    const groups = Array.from(map.values());
    groups.sort((a, b) => a.min - b.min);
    return groups;
  }, [isPackagePricing, normalizedPackages]);

  const hasPeopleDependentPackagePricing = useMemo(() => {
    if (!isPackagePricing) return false;
    // Treat any defined person group (even a single 1–50 group) as "people dependent"
    // so we always use personGroups in package mode instead of generic fallback ranges.
    return personGroups.length > 0;
  }, [isPackagePricing, personGroups]);

  const [selectedPackageId, setSelectedPackageId] = useState(null);
  const [selectedPeopleGroupKey, setSelectedPeopleGroupKey] = useState(null);
  const [showPackageDropdownMain, setShowPackageDropdownMain] = useState(false);
  const [showPackageDropdownModal, setShowPackageDropdownModal] = useState(false);
  const [selectedPackageTimeBlock, setSelectedPackageTimeBlock] = useState(null);

  // Initialize/sync defaults for package pricing: smallest package & smallest people group,
  // but prefer values that already exist in the global booking store.
  // This also repairs local sidebar state after the booking store is reset.
  useEffect(() => {
    if (!isPackagePricing || normalizedPackages.length === 0) return;

    const selectedOrDefaultPackage =
      normalizedPackages.find((pkg) => pkg.id === bookingData?.packageId) ||
      normalizedPackages[0];
    const nextPackageId = selectedOrDefaultPackage?.id || null;

    if (selectedPackageId !== nextPackageId) {
      setSelectedPackageId(nextPackageId);
    }

    if (nextPackageId && bookingData?.packageId !== nextPackageId) {
      updateBookingData('packageId', nextPackageId);
    }

    if (selectedOrDefaultPackage) {
      const duration = selectedOrDefaultPackage.durationHours ?? null;
      const name =
        selectedOrDefaultPackage.name ||
        (typeof duration === 'number'
          ? `${duration} ${duration === 1 ? 'Stunde' : 'Stunden'}`
          : 'Paket');

      if (bookingData?.packageName !== name) {
        updateBookingData('packageName', name);
      }

      if (bookingData?.packageDurationHours !== duration) {
        updateBookingData('packageDurationHours', duration);
      }
    }

    const nextPeopleGroupKey =
      personGroups.length > 0
        ? (bookingData?.selectedPeopleGroupKey || personGroups[0].key)
        : null;
    if (selectedPeopleGroupKey !== nextPeopleGroupKey) {
      setSelectedPeopleGroupKey(nextPeopleGroupKey);
    }

    if (nextPeopleGroupKey && bookingData?.selectedPeopleGroupKey !== nextPeopleGroupKey) {
      updateBookingData('selectedPeopleGroupKey', nextPeopleGroupKey);
    }
  }, [
    isPackagePricing,
    normalizedPackages,
    personGroups,
    bookingData?.packageId,
    bookingData?.packageName,
    bookingData?.packageDurationHours,
    bookingData?.selectedPeopleGroupKey,
    selectedPackageId,
    selectedPeopleGroupKey,
    updateBookingData,
  ]);

  const getSelectedPackage = () => {
    if (!isPackagePricing || normalizedPackages.length === 0) return null;
    // Prefer bookingData.packageId (from BookingFirst) over local state
    const packageIdToUse = bookingData?.packageId || selectedPackageId;
    const found = normalizedPackages.find((pkg) => pkg.id === packageIdToUse);
    return found || normalizedPackages[0];
  };

  // Helper: get base price for a given package + people group key
  const getPackageBasePriceForGroup = (pkg, groupKey) => {
    if (!pkg || !Array.isArray(pkg.prices) || pkg.prices.length === 0) return 0;

    const priceEntry = resolvePackagePriceEntry(pkg.prices, groupKey) || pkg.prices[0];

    const rawPrice = priceEntry?.price;
    const num = typeof rawPrice === 'number' ? rawPrice : parseFloat(rawPrice || '0');
    return Number.isFinite(num) ? num : 0;
  };

  const getSelectedPackageBasePrice = () => {
    const pkg = getSelectedPackage();
    if (!pkg || !Array.isArray(pkg.prices) || pkg.prices.length === 0) return 0;
    // Always use the cheapest group (first price entry) for the base studio package price
    // so the packagePriceBox shows the minimal studio price.
    const lowestPriceEntry = pkg.prices[0];
    const rawPrice = lowestPriceEntry?.price;
    const num = typeof rawPrice === 'number' ? rawPrice : parseFloat(rawPrice || '0');
    return Number.isFinite(num) ? num : 0;
  };

  // Get the base price for any package (lowest price entry, without people group calculation)
  const getPackageBasePrice = (pkg) => {
    if (!pkg || !Array.isArray(pkg.prices) || pkg.prices.length === 0) return 0;
    const lowestPriceEntry = pkg.prices[0];
    const rawPrice = lowestPriceEntry?.price;
    const num = typeof rawPrice === 'number' ? rawPrice : parseFloat(rawPrice || '0');
    return Number.isFinite(num) ? num : 0;
  };

  // Helper: additional cost label for a people group (relative to cheapest group of this package)
  const getAdditionalCostLabelForGroup = (pkg, groupKey) => {
    if (!pkg || !Array.isArray(pkg.prices) || pkg.prices.length === 0) return '';

    const lowestPriceEntry = pkg.prices[0];
    const rawLowest = lowestPriceEntry?.price;
    const lowest = typeof rawLowest === 'number' ? rawLowest : parseFloat(rawLowest || '0');

    const groupBase = getPackageBasePriceForGroup(pkg, groupKey);
    const extra = Math.max(0, groupBase - lowest);

    if (!extra) return '';
    return ` (+${extra} €)`;
  };

  // --- Sync package selection into global booking store (for BookingFirst etc.) ---
  useEffect(() => {
    if (!isPackagePricing || !selectedPackageId) return;

    updateBookingData('packageId', selectedPackageId);

    const pkg = normalizedPackages.find((p) => p.id === selectedPackageId);
    if (pkg) {
      const duration = pkg.durationHours;
      const name =
        pkg.name ||
        (typeof duration === 'number'
          ? `${duration} ${duration === 1 ? 'Stunde' : 'Stunden'}`
          : 'Paket');

      updateBookingData('packageName', name);
      updateBookingData('packageDurationHours', duration);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPackagePricing, selectedPackageId, normalizedPackages]);

  useEffect(() => {
    if (!isPackagePricing || !selectedPeopleGroupKey) return;
    updateBookingData('selectedPeopleGroupKey', selectedPeopleGroupKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPackagePricing, selectedPeopleGroupKey]);

  // Time Slot Generation Functions
  const getAvailableTimeRanges = () => {
    if (!bookingData.date) return [];
    return getAvailabilityRangesForDate(bookingData.date, bookingStudioData);
  };

  // Generate contiguous time blocks for the selected package duration on the current date
  const generatePackageTimeBlocks = () => {
    if (!isPackagePricing) return [];
    const pkg = getSelectedPackage();
    const durationHours = pkg?.durationHours || 0;
    if (!durationHours || !bookingData.date) return [];

    const timeRanges = getAvailableTimeRanges();
    const blocks = [];

    timeRanges.forEach((range) => {
      const { start, end } = range;
      const startDate = new Date(`2000-01-01T${start}`);
      const endDate = new Date(`2000-01-01T${end === "00:00" || end === "24:00" ? "24:00" : end}`);

      const latestStartTime = new Date(endDate);
      latestStartTime.setHours(latestStartTime.getHours() - durationHours);
      if (startDate > latestStartTime) {
        return;
      }

      let current = new Date(startDate);
      while (current <= latestStartTime) {
        const blockStart = `${current.getHours().toString().padStart(2, '0')}:00`;
        const endBlockDate = new Date(current);
        endBlockDate.setHours(endBlockDate.getHours() + durationHours);
        const endHour = endBlockDate.getHours();
        const blockEnd = endHour === 0 || endHour === 24
          ? "24:00"
          : `${endHour.toString().padStart(2, '0')}:00`;

        if (
          isBookingRangeAvailable(
            bookingData.date,
            blockStart,
            durationHours,
            bookingStudioData
          )
        ) {
          blocks.push({ start: blockStart, end: blockEnd });
        }

        current.setHours(current.getHours() + 1);
      }
    });

    const seen = new Set();
    const uniqueBlocks = [];
    blocks.forEach((b) => {
      const key = `${b.start}-${b.end}`;
      if (!seen.has(key)) {
        seen.add(key);
        uniqueBlocks.push(b);
      }
    });

    uniqueBlocks.sort((a, b) => (a.start > b.start ? 1 : -1));
    return uniqueBlocks;
  };

  const generateTimeSlots = () => {
    if (!bookingData.date) return [];

    const timeRanges = getAvailableTimeRanges();
    let allSlots = [];

    // Process each time range and collect all valid slots
    timeRanges.forEach(range => {
      const { start, end } = range;

      // Parse the start and end times
      const startDate = new Date(`2000-01-01T${start}`);
      const endDate = new Date(`2000-01-01T${end === "00:00" || end === "24:00" ? "24:00" : end}`);

      // Calculate the latest possible hour considering minimum booking duration
      const latestStartTime = new Date(endDate);
      latestStartTime.setHours(latestStartTime.getHours() - minHours);


      // Skip this range if it's too short for minimum booking duration
      if (startDate > latestStartTime) {
        return;
      }

      // Generate time slots from the start time up to the latest possible start time (inclusive)
      let current = new Date(startDate);
      while (current <= latestStartTime) {
        const timeString = current.getHours() === 24 ? "24:00" :
                         `${current.getHours().toString().padStart(2, '0')}:00`;


        if (
          isBookingRangeAvailable(
            bookingData.date,
            timeString,
            minHours,
            bookingStudioData
          )
        ) {
          allSlots.push(timeString);
        }

        current.setHours(current.getHours() + 1);
      }
    });

    // Remove duplicates using Set
    const uniqueSlots = [...new Set(allSlots)];

    // Sort the time slots from early to late
    uniqueSlots.sort((a, b) => {
      const [hoursA] = a.split(':').map(Number);
      const [hoursB] = b.split(':').map(Number);

      // Special case for 24:00
      if (a === "24:00") return 1;  // 24:00 goes last
      if (b === "24:00") return -1; // 24:00 goes last

      // Normal hour comparison
      return hoursA - hoursB;
    });

    return uniqueSlots;
  };

  const generateEndTimeSlots = (startTime) => {
    if (!startTime || !bookingData.date) {
      return [];
    }

    const timeRanges = getAvailableTimeRanges();

    if (timeRanges.length === 0) {
      return [];
    }

    const slots = [];
    // Use the actual selected date for time calculations to handle timezone properly
    const selectedDate = parseLocalDateString(bookingData.date);
    const startDateTime = new Date(`${bookingData.date}T${startTime}`);

    // Find the specific time range that contains the start time
    // Fix: Use the same time format (HH:00) for comparison
    const startTimeHour = startTime.split(':')[0].padStart(2, '0') + ":00";


    const currentRange = timeRanges.find(range => {
      // Convert all times to 24-hour format for proper comparison
      const rangeStart = range.start;
      const rangeEnd = range.end;

      // Compare as strings directly for hour-based ranges
      const isStartInRange =
        // Handle special case where range crosses midnight
        (rangeEnd < rangeStart) ?
          (startTimeHour >= rangeStart || startTimeHour <= rangeEnd) :
          (startTimeHour >= rangeStart && startTimeHour <= rangeEnd);

      return isStartInRange;
    });


    if (!currentRange) {
      return [];  // Start time isn't in any available range
    }

    // Set endDateTime to the end of the current time range only
    let endDateTime = new Date(selectedDate);
    if (currentRange.end === "00:00" || currentRange.end === "24:00") {
      endDateTime.setHours(24, 0, 0, 0);
    } else {
      const [endHour, endMinute] = currentRange.end.split(':');
      endDateTime.setHours(parseInt(endHour), parseInt(endMinute), 0, 0);
    }


    // Start from minHours after the selected start time
    let current = new Date(startDateTime);
    current.setHours(current.getHours() + minHours);

    // If minimum booking puts us past the end time, there are no valid end times
    if (current > endDateTime) {
      return [];
    }

    // Find any bookings that could limit our end time
    const busyIntervalsOnSelectedDate = getBusyIntervalItems(bookingStudioData).filter(interval => {
      if (!interval || !interval.startDateTime || !interval.endDateTime) {
        return false;
      }

      const bookingDate = new Date(interval.startDateTime).toDateString();
      const ourDate = selectedDate.toDateString();
      return bookingDate === ourDate;
    }) || [];


    // Find the earliest booking that starts after our selected start time
    // but only consider bookings within our current availability range
    let earliestNextBooking = null;
    busyIntervalsOnSelectedDate.forEach(interval => {
      const bookingStart = new Date(interval.startDateTime);
      if (bookingStart > startDateTime && bookingStart <= endDateTime) {
        if (!earliestNextBooking || bookingStart < earliestNextBooking) {
          earliestNextBooking = bookingStart;
        }
      }
    });


    // If there's a booking after our start time within our range, limit end times to that booking
    if (earliestNextBooking && earliestNextBooking < endDateTime) {
      endDateTime = earliestNextBooking;
    }

    // Generate all valid end times from minHours after start to the end of the current range
    while (current <= endDateTime) {
      // Always display 24:00 instead of 00:00 for end times
      const timeString = current.getHours() === 0 || current.getHours() === 24 ? "24:00" :
                       `${current.getHours().toString().padStart(2, '0')}:00`;


      // Check if this end time would overlap with any existing bookings
      const wouldOverlap = getBusyIntervalItems(bookingStudioData).some(interval => {
        if (!interval || !interval.startDateTime || !interval.endDateTime) {
          return false;
        }

        const bookingStart = new Date(interval.startDateTime);
        const bookingEnd = new Date(interval.endDateTime);

        // End time is invalid if it's within an existing booking
        // Exception: The exact start time of a booking is valid as an end time
        if (current.getTime() !== bookingStart.getTime() &&
            current > bookingStart && current < bookingEnd) {
          return true;
        }

        return false;
      });

      // Check if this end time would overlap with any blocked times
      const wouldOverlapBlocked = bookingStudioData?.blockedTimes?.items?.some(blocked => {
        if (!blocked || !blocked.startDateTime || !blocked.endDateTime) {
          return false;
        }

        const blockedStart = new Date(blocked.startDateTime);
        const blockedEnd = new Date(blocked.endDateTime);

        // End time is invalid if it's within a blocked time
        // Exception: The exact start time of a blocked period is valid as an end time
        if (current.getTime() !== blockedStart.getTime() &&
            current > blockedStart && current <= blockedEnd) {
          return true;
        }

        return false;
      });


      const durationHours = (current - startDateTime) / (1000 * 60 * 60);
      const isRangeAvailable = isBookingRangeAvailable(
        bookingData.date,
        startTime,
        durationHours,
        bookingStudioData
      );

      if (!wouldOverlap && !wouldOverlapBlocked && isRangeAvailable) {
        slots.push(timeString);
      }

      current.setHours(current.getHours() + 1);
    }

    return slots;
  };

  // Update available times when date changes (hourly mode only)
  useEffect(() => {
    // In package pricing mode, start/end times are controlled by package time blocks.
    // Do NOT run the hourly normalization here, otherwise package endTime can be cleared.
    if (isPackagePricing) {
      setTimes([]);
      return;
    }

    if (!bookingData.date) {
      setTimes([]);
      return;
    }

    const availableSlots = generateTimeSlots();

    setTimes(availableSlots);

    if (bookingData.startTime && !availableSlots.includes(bookingData.startTime)) {
      setStartTime('');
      updateBookingData('startTime', '');
      setEndTime('');
      updateBookingData('endTime', '');
    } else if (bookingData.startTime) {
      const endSlots = generateEndTimeSlots(bookingData.startTime);

      if (bookingData.endTime && !endSlots.includes(bookingData.endTime)) {
        setEndTime('');
        updateBookingData('endTime', '');
      }
    }
  }, [
    bookingData.date,
    bookingData.endTime,
    bookingData.startTime,
    bookingStudioData,
    isPackagePricing,
    minHours,
  ]);

  // Updated Helper function to check if a specific date is available
  const isDateAvailable = (date) => {
    if (!bookingStudioData) {
      return false;
    }

    const dateObj = parseLocalDateString(date) || new Date(date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    dateObj.setHours(0, 0, 0, 0);

    // Date must not be in the past (but today is allowed)
    if (dateObj < today) {
      return false;
    }

    const durationHoursForDate =
      isPackagePricing ? getBookingDurationHours(bookingStudioData, bookingData) : minHours || 1;

    return hasBookableTimeSlot(dateObj, bookingStudioData, {
      durationHours: durationHoursForDate,
    });
  };

  // Helper function to find the next available date
  const findNextAvailableDate = () => {
    const today = new Date();

    // Check if today is available using the comprehensive check
    const todayFormatted = formatLocalDate(today);

    // For today, we need to check if there are any available time slots after current time
    if (isDateAvailable(todayFormatted)) {
      return todayFormatted;
    }

    // Find next available date within 30 days
    for (let i = 1; i <= 30; i++) {
      const dateToCheck = new Date(today);
      dateToCheck.setDate(today.getDate() + i);
      const formattedDate = formatLocalDate(dateToCheck);

      if (isDateAvailable(formattedDate)) {
        return formattedDate;
      }
    }

    return null;
  };

  // Initialize booking data from backend when component loads
  useEffect(() => {
    if (!bookingStudioData) {
      return;
    }

    // Check if we need to set or validate the date
    const needsDateSet = !bookingData.date;
    const needsDateValidation = bookingData.date && !isDateAvailable(bookingData.date);

    if (needsDateSet) {
      const nextAvailableDate = findNextAvailableDate();
      if (nextAvailableDate) {
        setDate(parseLocalDateString(nextAvailableDate));
        updateBookingData('date', nextAvailableDate);

        // Reset times when date changes
        setStartTime('');
        updateBookingData('startTime', '');
        setEndTime('');
        updateBookingData('endTime', '');
      }
    } else if (needsDateValidation) {
      // Only change date if it's actually invalid, and do a more gentle check
      const currentDate = bookingData.date;
      const isCurrentDateValid = isDateAvailable(currentDate);

      if (!isCurrentDateValid) {
        const nextAvailableDate = findNextAvailableDate();
        if (!nextAvailableDate) {
          setDate(null);
          updateBookingData('date', '');
          setStartTime('');
          updateBookingData('startTime', '');
          setEndTime('');
          updateBookingData('endTime', '');
        } else {
          setDate(parseLocalDateString(nextAvailableDate));
          updateBookingData('date', nextAvailableDate);

          // Reset times when date changes
          setStartTime('');
          updateBookingData('startTime', '');
          setEndTime('');
          updateBookingData('endTime', '');
        }
      }
    }

    if (!bookingData.numberOfPeople) {
      updateBookingData('numberOfPeople', '1-5');
      updateBookingData('addFreelancer', false);
    }
  }, [bookingStudioData]);

  // Sync component state with booking store
  useEffect(() => {
    setDate(parseLocalDateString(bookingData.date));
    setStartTime(bookingData.startTime || '');
    setEndTime(bookingData.endTime || '');
    setNumberOfPeople(bookingData.numberOfPeople || "1-5");
  }, [bookingData]);

  // Handler for clicks outside date picker
  useEffect(() => {
    const handleClickOutside = (event) => {

      // Check if click is inside the datePickerRef
      const isInsideRef = datePickerRef.current && datePickerRef.current.contains(event.target);

      // Check if click is on any react-datepicker element (including navigation buttons)
      // Use closest() to check all parent elements, not just the clicked element itself
      const closestDatePicker = event.target.closest('.react-datepicker');
      const closestNavigation = event.target.closest('.react-datepicker__navigation');
      const closestHeader = event.target.closest('.react-datepicker__header');
      const closestMonthContainer = event.target.closest('.react-datepicker__month-container');
      const closestMonth = event.target.closest('.react-datepicker__month');
      const closestWeek = event.target.closest('.react-datepicker__week');

      const isDatePickerElement =
        closestDatePicker !== null ||
        closestNavigation !== null ||
        closestHeader !== null ||
        closestMonthContainer !== null ||
        closestMonth !== null ||
        closestWeek !== null;

      // Only close if click is outside both the ref and any DatePicker element
      if (!isInsideRef && !isDatePickerElement) {
        setShowDatePicker(false);
      } else {

      }
    };

    if (showDatePicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDatePicker]);

  // Handler for clicks outside people dropdown (main card)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (peopleDropdownMainRef.current && !peopleDropdownMainRef.current.contains(event.target)) {
        setShowPeopleDropdownMain(false);
      }
    };

    if (showPeopleDropdownMain) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPeopleDropdownMain]);

  // Handler for clicks outside people dropdown (modal)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (peopleDropdownModalRef.current && !peopleDropdownModalRef.current.contains(event.target)) {
        setShowPeopleDropdownModal(false);
      }
    };

    if (showPeopleDropdownModal) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPeopleDropdownModal]);

  // Handler for clicks outside package dropdown (main card)
  useEffect(() => {
    const handleClickOutsidePackage = (event) => {
      if (packageDropdownMainRef.current && !packageDropdownMainRef.current.contains(event.target)) {
        setShowPackageDropdownMain(false);
      }
    };

    if (showPackageDropdownMain) {
      document.addEventListener('mousedown', handleClickOutsidePackage);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutsidePackage);
    };
  }, [showPackageDropdownMain]);

  // Handler for clicks outside package dropdown (modal)
  useEffect(() => {
    const handleClickOutsidePackage = (event) => {
      if (packageDropdownModalRef.current && !packageDropdownModalRef.current.contains(event.target)) {
        setShowPackageDropdownModal(false);
      }
    };

    if (showPackageDropdownModal) {
      document.addEventListener('mousedown', handleClickOutsidePackage);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutsidePackage);
    };
  }, [showPackageDropdownModal]);

  // Event handlers
  const handleStartTimeChange = (e) => {
    const newStartTime = e.target.value;
    setStartTime(newStartTime);
    updateBookingData('startTime', newStartTime);

    // Clear validation error when user selects a time
    if (timeValidationError) {
      setTimeValidationError(false);
    }

    // Reset end time when start time changes
    setEndTime('');
    updateBookingData('endTime', '');
  };

  const handleEndTimeChange = (e) => {
    const newEndTime = e.target.value;
    setEndTime(newEndTime);
    updateBookingData('endTime', newEndTime);

    // Clear validation error when user selects an end time
    if (timeValidationError) {
      setTimeValidationError(false);
    }
  };

  const handleNumberOfPeopleChange = (e) => {
    setNumberOfPeople(e.target.value);
    updateBookingData('numberOfPeople', e.target.value);
  };

  const handleAddFreelancerChange = () => {
    setAddFreelancer(!addFreelancer);
    updateBookingData('addFreelancer', !bookingData.addFreelancer);
  };

  const handleSignInClick = () => {
    setShowSignIn(true);
  };

  // Function to generate people options for hourly / package modes
  const generatePeopleOptions = () => {
    // In package mode we always rely on personGroups derived from package prices
    // and never use these generic ranges.
    if (isPackagePricing) {
      return [];
    }

    // If studio has explicit tieredPricing (hourly people groups), use those
    if (Array.isArray(bookingStudioData?.tieredPricing) && bookingStudioData.tieredPricing.length > 0) {
      const capacity = bookingStudioData?.numberOfPeople || 0;
      return bookingStudioData.tieredPricing
        .slice()
        .sort((a, b) => (a.minPersons || 0) - (b.minPersons || 0))
        .map(tier => {
          const min = tier.minPersons;
          const max = tier.maxPersons;
          const clampedMax = capacity ? Math.min(max, capacity) : max;
          const value = `${min}-${clampedMax}`;
          const label = min === clampedMax
            ? `${min} Personen`
            : `${min}-${clampedMax} Personen`;
          return { min, max: clampedMax, label, value };
        });
    }

    // Fallback: generic ranges based on numberOfPeople
    const ranges = [
      { min: 1, max: 5, label: "1-5 Personen", value: "1-5" },
      { min: 6, max: 10, label: "6-10 Personen", value: "6-10" },
      { min: 11, max: 20, label: "11-20 Personen", value: "11-20" },
      { min: 21, max: 40, label: "21-40 Personen", value: "21-40" },
      { min: 41, max: 60, label: "41-60 Personen", value: "41-60" },
      { min: 61, max: Infinity, label: "60+ Personen", value: "60+" }
    ];

    const availableOptions = [];

    for (const range of ranges) {
      if (numberOfPeople >= range.min) {
        availableOptions.push(range);
      } else {
        break;
      }
    }

    return availableOptions;
  };

  const calculateDuration = (start, end) => {
    if (!start || !end) return 0;
    const startDate = new Date(`2000-01-01T${start}`);
    const normalizedEnd = end === "00:00" || end === "24:00" ? "00:00" : end;
    const endDate = new Date(`2000-01-01T${normalizedEnd}`);

    if (end === "00:00" || end === "24:00") {
      endDate.setDate(endDate.getDate() + 1);
    }
    const diffMs = endDate - startDate;
    const diffHours = diffMs / (1000 * 60 * 60);

    return diffHours;
  };

  const duration = calculateDuration(bookingData.startTime || startTime, bookingData.endTime || endTime);
  const studioHourly = (!isPackagePricing && duration > 0 && bookingData.baseStudioPrice)
    ? Math.round((bookingData.baseStudioPrice / duration) * 100) / 100
    : 0;
  const packageTimeBlocks = useMemo(
    () => generatePackageTimeBlocks(),
    [isPackagePricing, bookingData.date, selectedPackageId, selectedPeopleGroupKey, bookingStudioData]
  );

  // Helper to show current hourly price in header (respects tieredPricing groups)
  const getCurrentHourlyPrice = () => {
    if (isPackagePricing) return null;

    const hasTieredHourlyPricing =
      Array.isArray(bookingStudioData?.tieredPricing) &&
      bookingStudioData.tieredPricing.length > 0;

    // If no tieredPricing, fall back to flat studioPrice from backend
    if (!hasTieredHourlyPricing) {
      return studioPrice;
    }

    const options = generatePeopleOptions();
    const currentValue = bookingData?.numberOfPeople;
    const selectedOption = options.find(o => o.value === currentValue) || options[0];
    if (!selectedOption) {
      return studioPrice;
    }

    const matchingTier = bookingStudioData.tieredPricing.find(tier =>
      tier.minPersons === selectedOption.min && tier.maxPersons === selectedOption.max
    );

    return matchingTier?.pricePerHour || studioPrice;
  };

  const requireAuthenticatedUser = () => {
    if (hasAuthenticatedUser) {
      return true;
    }

    if (!isPhoneSize) {
      const params = new URLSearchParams(location.search);
      params.set("booking", "open");
      const search = params.toString();

      storeRedirectPathTo({
        pathname: location.pathname,
        search: search ? `?${search}` : "",
        hash: location.hash,
      });
      handleSignInClick();
      return false;
    }

    const params = new URLSearchParams(location.search);
    params.set("booking-phone", "open");
    const search = params.toString();
    const from = {
      pathname: location.pathname,
      search: search ? `?${search}` : "",
      hash: location.hash,
    };

    storeRedirectPathTo(from);
    navigate("/anmelden", {
      state: { background: location, from, requiresAuth: true },
    });
    return false;
  };

  const startBooking = () => {
    if (isCurrentUserStudioOwner) {
      return;
    }

    if (!requireAuthenticatedUser()) {
      return;
    }

    // Bypass date/time validation if addFreelancer is true
    // User can select date/time in the booking procedure modal
    if (!bookingData.addFreelancer) {
      // Validate that both start and end times are selected
      if (!bookingData.startTime || !bookingData.endTime) {
        setTimeValidationError(true);
        return;
      }
    }

    // Clear any existing validation error
    setTimeValidationError(false);

    setShowBookingProcedure(true);
  }

  const openPackageBooking = () => {
    if (isCurrentUserStudioOwner) {
      return;
    }

    if (!requireAuthenticatedUser()) {
      return;
    }

    if (setPackageModalFromOverlay) {
      setPackageModalFromOverlay(false);
    }
    setShowPackageModal(true);
  };

  const hasTieredHourlyPricing =
    !isPackagePricing &&
    Array.isArray(bookingStudioData?.tieredPricing) &&
    bookingStudioData.tieredPricing.length > 0;

  const useNumericPeopleInput = !isPackagePricing && !hasTieredHourlyPricing;

  const studioCapacity = useMemo(() => {
    const raw = bookingStudioData?.numberOfPeople;
    if (typeof raw === 'number') return raw || 0;
    const parsed = parseInt(raw || '0', 10);
    return Number.isNaN(parsed) ? 0 : parsed;
  }, [bookingStudioData?.numberOfPeople]);

  const numericPeopleValue = useMemo(() => {
    const raw = bookingData?.numberOfPeople;
    if (typeof raw === 'number') return raw;
    if (typeof raw === 'string') {
      const match = raw.match(/^(\d+)/);
      if (match) {
        const parsed = parseInt(match[1], 10);
        if (!Number.isNaN(parsed)) return parsed;
      }
    }
    return 1;
  }, [bookingData?.numberOfPeople]);

  // Reset selected time block when package or date changes
  useEffect(() => {
    setSelectedPackageTimeBlock(null);
  }, [selectedPackageId, bookingData.date]);

  const handleDatePickerContinue = () => {
    setShowDatePicker(false);
  };

  const toggleDatePicker = (e) => {
    // Don't toggle if clicking inside the date picker overlay
    if (datePickerRef.current && datePickerRef.current.contains(e.target)) {
      return;
    }
    setShowDatePicker(!showDatePicker);
  };

  const handleOverlayClick = (e) => {
    e.stopPropagation();
  };

  const togglePeopleDropdownMain = () => {
    setShowPeopleDropdownMain((prev) => !prev);
  };

  const togglePeopleDropdownModal = () => {
    setShowPeopleDropdownModal((prev) => !prev);
  };

  const togglePackageDropdownMain = () => {
    setShowPackageDropdownMain((prev) => !prev);
  };

  const togglePackageDropdownModal = () => {
    setShowPackageDropdownModal((prev) => !prev);
  };

  const handlePeopleOptionSelect = (optionValue, source) => {
    if (isPackagePricing && hasPeopleDependentPackagePricing) {
      // In package mode we store the selected people group key
      setSelectedPeopleGroupKey(optionValue);
      updateBookingData('selectedPeopleGroupKey', optionValue);

      // Calculate the people group surcharge
      const pkg = getSelectedPackage();
      if (pkg && Array.isArray(pkg.prices)) {
        const selectedPriceEntry = resolvePackagePriceEntry(pkg.prices, optionValue);

        // Get lowest group price for comparison
        const lowestPriceEntry = pkg.prices[0];
        const lowestPrice = typeof lowestPriceEntry?.price === 'number' ? lowestPriceEntry.price : parseFloat(lowestPriceEntry?.price || '0');
        const selectedPrice = selectedPriceEntry ? (typeof selectedPriceEntry.price === 'number' ? selectedPriceEntry.price : parseFloat(selectedPriceEntry.price || '0')) : lowestPrice;

        const peopleExtra = Math.max(0, selectedPrice - lowestPrice);
        updateBookingData('packagePeoplePrice', peopleExtra);
        // Central pricing sync (useBookingPricingSync) recomputes totalStudioPrice + totalPrice
        // from selectedPeopleGroupKey — no manual write needed here.
      }
    } else {
      setNumberOfPeople(optionValue);
      updateBookingData('numberOfPeople', optionValue);
    }

    if (source === 'modal') {
      setShowPeopleDropdownModal(false);
    } else {
      setShowPeopleDropdownMain(false);
    }
  };

  const handlePeopleNumericChange = (e) => {
    let value = e.target.value;
    // Allow empty input so user can clear and retype
    if (value === '') {
      updateBookingData('numberOfPeople', '');
      setNumberOfPeople('');
      return;
    }

    let num = parseInt(value, 10);
    if (Number.isNaN(num)) {
      return;
    }

    if (num < 1) num = 1;
    if (studioCapacity && num > studioCapacity) num = studioCapacity;

    updateBookingData('numberOfPeople', num);
    setNumberOfPeople(num);
  };

  const handlePackageOptionSelect = (packageId, source) => {
    // Check if this is actually a different package (not just re-selecting the same one)
    const isPackageChange = bookingData?.packageId !== packageId;

    setSelectedPackageId(packageId);

    // Update booking data with selected package info
    const selectedPkg = normalizedPackages.find(p => p.id === packageId);
    if (selectedPkg) {
      updateBookingData('packageId', packageId);
      updateBookingData('packageName', selectedPkg.name || `${selectedPkg.durationHours} ${selectedPkg.durationHours === 1 ? 'Stunde' : 'Stunden'}`);
      updateBookingData('packageDurationHours', selectedPkg.durationHours);

      // If package changed, clear date and time
      if (isPackageChange) {
        updateBookingData('date', '');
        updateBookingData('startTime', '');
        updateBookingData('endTime', '');
        setDate(null);
        setStartTime('');
        setEndTime('');
        setSelectedPackageTimeBlock(null);
      }

      // Calculate and store the package price for the CURRENT people group.
      // IMPORTANT: do NOT rely on selectedPackageId here (setState is async),
      // use the selectedPkg we already have plus the current/group key from store.
      const groupKey =
        bookingData?.selectedPeopleGroupKey ||
        selectedPeopleGroupKey ||
        (personGroups.length > 0 ? personGroups[0].key : null);

      const totalBasePrice = getPackageBasePriceForGroup(selectedPkg, groupKey);
      const lowestBase = getPackageBasePrice(selectedPkg);

      updateBookingData('packageBasePrice', totalBasePrice > 0 ? lowestBase : 0);
      updateBookingData('packagePeoplePrice', totalBasePrice > lowestBase ? totalBasePrice - lowestBase : 0);
    }

    if (source === 'modal') {
      setShowPackageDropdownModal(false);
    } else {
      setShowPackageDropdownMain(false);
    }
  };

  // Calculate available end times based on selected start time
  const availableEndTimes = bookingData.startTime ? generateEndTimeSlots(bookingData.startTime) : [];

  const handlePackageTimeBlockSelect = (block) => {
    const key = `${block.start}-${block.end}`;
    setSelectedPackageTimeBlock(key);
    updateBookingData('startTime', block.start);
    updateBookingData('endTime', block.end);
  };

  const handlePackageModalConfirm = () => {
    if (!bookingData.date || !selectedPackageTimeBlock) return;

    // If the modal was opened from the overlay (BookingFirst), just save changes.
    if (packageModalFromOverlay) {
      setShowPackageModal(false);
      return;
    }

    if (isCurrentUserStudioOwner || !requireAuthenticatedUser()) {
      return;
    }

    setTimeValidationError(false);

    // Replace the package-modal URL entry with the booking flow in one step.
    // Closing via navigate(-1) and then pushing booking=open can otherwise race
    // and leave both params active, which reopens this modal over Buchungsdetails.
    const params = new URLSearchParams(location.search);
    params.delete('package-modal');
    params.set('booking', 'open');
    const search = params.toString();

    navigate(
      {
        pathname: location.pathname,
        search: search ? `?${search}` : '',
        hash: location.hash,
      },
      { replace: true }
    );
  };

  const packageModalButtonLabel = packageModalFromOverlay ? 'Änderung speichern' : 'Buchung starten';

  const canStartPackageBooking = Boolean(
    bookingData.date &&
    selectedPackageTimeBlock &&
    !bookingData?.pricingUnavailable
  );

  const displayPrice = calculatePreExtrasTotal(bookingData);
  const displayPriceLabel = bookingData?.pricingUnavailable ? 'Nicht verfügbar' : `${displayPrice} €`;

  // Helper function to get weekend discount/surcharge info
  const getWeekendDiscountInfo = () => {
    if (!bookingData.date || !isPackagePricing) return null;

    if (!bookingStudioData || !bookingStudioData.weekendSurcharge) return null;

    const selectedDate = parseLocalDateString(bookingData.date);
    const dayOfWeek = getDay(selectedDate); // 0 = Sunday, 6 = Saturday

    const weekendSurcharge = bookingStudioData.weekendSurcharge;

    // Check for Saturday (6) or Sunday (0)
    if (dayOfWeek === 6 && weekendSurcharge.saturdayEnabled && weekendSurcharge.saturdaySurchargeValue != null) {
      const value = parseFloat(weekendSurcharge.saturdaySurchargeValue);
      if (!isNaN(value) && value !== 0) {
        return {
          day: 'Samstag',
          type: weekendSurcharge.saturdaySurchargeType,
          value: weekendSurcharge.saturdaySurchargeValue
        };
      }
    }

    if (dayOfWeek === 0 && weekendSurcharge.sundayEnabled && weekendSurcharge.sundaySurchargeValue != null) {
      const value = parseFloat(weekendSurcharge.sundaySurchargeValue);
      if (!isNaN(value) && value !== 0) {
        return {
          day: 'Sonntag',
          type: weekendSurcharge.sundaySurchargeType,
          value: weekendSurcharge.sundaySurchargeValue
        };
      }
    }

    return null;
  };

  return (
    <div className={classes.outsideContainer}>
      {isPackagePricing && (
        <SmallModal
          open={showPackageModal}
          onBackdropClick={() => setShowPackageModal(false)}
          className={classes.packageModal}
          fromBookingDetails
        >
          <div className={classes.packageModalContent}>
            <div className={classes.packageModalHeader}>
              <h3 className="titleSemibold3">Datum &amp; Uhrzeit auswählen</h3>
              <button
                type="button"
                className={classes.packageModalClose}
                onClick={() => setShowPackageModal(false)}
              >
                ✕
              </button>
            </div>

            <div className={classes.packageModalHeaderContent}>
              <div className={classes.labelsContainerModal}>
                <div className={classes.dateLabelContainer} style={{ margin: '0px' }}>
                  <div className={classes.customDropdownContainer} ref={packageDropdownModalRef}>
                    <div
                      className={`${classes.packageLabelContainer} regularLarge`}
                      onClick={togglePackageDropdownModal}
                    >
                      <div className={classes.packageContent}>
                        <span>
                          {(() => {
                            const pkg = getSelectedPackage();
                            if (!pkg) return 'Paket auswählen';
                            return `${pkg.durationHours} ${pkg.durationHours === 1 ? 'Stunde' : 'Stunden'}`;
                          })()}
                        </span>
                      <div className={classes.packagePriceBox}>
                        {getSelectedPackageBasePrice()} €
                      </div>
                      </div>
                      <svg xmlns="http://www.w3.org/2000/svg" width="15" height="8" viewBox="0 0 15 8" fill="none">
                        <path d="M1 0.86149L7.59865 6.8457M6.85146 6.82992L14 0.845703" stroke="#1F1F1F"/>
                      </svg>
                    </div>
                    {showPackageDropdownModal && (
                      <div className={classes.customDropdownOptions}>
                        {normalizedPackages.map((pkg) => (
                          <div
                            key={pkg.id}
                            className={`${classes.customDropdownOption} regularLarge`}
                            onClick={() => handlePackageOptionSelect(pkg.id, 'modal')}
                          >
                            <span>
                              {pkg.durationHours} {pkg.durationHours === 1 ? 'Stunde' : 'Stunden'}
                            </span>
                            <span style={{ marginLeft: '8px' }}>
                              {(() => {
                                // Get base price directly from package (lowest price entry, no people group calculation)
                                const basePrice = getPackageBasePrice(pkg);
                                return basePrice > 0 ? `${basePrice} €` : '';
                              })()}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className={classes.containerPeopleModal}>
                  <div className={classes.customDropdownContainer} ref={peopleDropdownModalRef}>
                    <div
                      className={`${classes.peopleLabelContainer} regularLarge`}
                      onClick={togglePeopleDropdownModal}
                    >
                      <label>
                        {(() => {
                          if (hasPeopleDependentPackagePricing) {
                            const group = personGroups.find(g => g.key === selectedPeopleGroupKey) || personGroups[0];
                            if (!group) return "Auswählen";
                            const pkg = getSelectedPackage();
                            const extraLabel = getAdditionalCostLabelForGroup(pkg, group.key);
                            return `${group.min}-${group.max} Personen${extraLabel}`;
                          }
                          return `Personenanzahl: ${bookingData?.numberOfPeople || "1-5"}`;
                        })()}
                      </label>
                      <svg xmlns="http://www.w3.org/2000/svg" width="15" height="8" viewBox="0 0 15 8" fill="none">
                        <path d="M1 0.86149L7.59865 6.8457M6.85146 6.82992L14 0.845703" stroke="#1F1F1F"/>
                      </svg>
                    </div>
                    {showPeopleDropdownModal && (
                      <div className={classes.customDropdownOptions}>
                        {hasPeopleDependentPackagePricing
                          ? personGroups.map((group) => (
                              <div
                                key={group.key}
                                className={`${classes.customDropdownOption} regularLarge`}
                                onClick={() => handlePeopleOptionSelect(group.key, 'modal')}
                              >
                                {(() => {
                                  const pkg = getSelectedPackage();
                                  const extraLabel = getAdditionalCostLabelForGroup(pkg, group.key);
                                  return `${group.min}-${group.max} Personen${extraLabel}`;
                                })()}
                              </div>
                            ))
                          : generatePeopleOptions().map((option) => (
                              <div
                                key={option?.value}
                                className={`${classes.customDropdownOption} regularLarge`}
                                onClick={() => handlePeopleOptionSelect(option?.value, 'modal')}
                              >
                                {option?.label}
                              </div>
                            ))}
                      </div>
                    )}
                  </div>
              </div>
            </div>

            <div className={classes.packageModalBody}>
              <div className={classes.packageModalLeft}>
                <div className={classes.packageModalDateChooser}>
                  <DateChooser
                    fromDesktop={true}
                    nextSlide={() => {}}
                    bookingStudioData={bookingStudioData}
                  />
                </div>
              </div>
              <div className={classes.packageModalRight}>
                <div className={classes.packageTimesSection}>
                  {!bookingData.date && (
                    <h3 className="semiboldMiddle">Verfügbare Uhrzeiten</h3>
                  )}
                  {!bookingData.date && (
                    <p className="regularMiddle">Bitte wähle zuerst ein Datum.</p>
                  )}
                  {bookingData.date && (
                    <>
                    <div className={classes.packageTimeBlockTitleContainer}>
                      <h4 className={`${classes.packageTimeBlockTitle} regularMiddle`}>
                        {format(parseLocalDateString(bookingData.date), 'EEEE, dd.MM.yyyy', { locale: de })}
                      </h4>
                      {(() => {
                        const weekendDiscount = getWeekendDiscountInfo();
                        if (weekendDiscount) {
                          const valueNum = parseFloat(weekendDiscount.value) || 0;
                          const isPercentage = weekendDiscount.type === 'PERCENTAGE';
                          const displayValue = isPercentage
                            ? `${Math.abs(valueNum)}%`
                            : `${Math.abs(valueNum)}€`;

                          return (
                            <p className={`regularMiddle ${classes.weekendDiscountInfo}`}>
                              {displayValue} Wochenendaufschlag
                            </p>
                          );
                        }
                        return null;
                      })()}
                      </div>
                      <div className={classes.packageTimeBlocksContainer}>
                        {packageTimeBlocks.length === 0 && (
                          <p className="regularMiddle">Keine verfügbaren Zeitfenster für dieses Paket.</p>
                        )}
                        {packageTimeBlocks.map((block) => {
                          const key = `${block.start}-${block.end}`;
                          const isSelected = key === selectedPackageTimeBlock;
                          return (
                            <div
                              key={key}
                              className={`${classes.packageTimeBlock} regularMiddle ${isSelected ? classes.packageTimeBlockSelected : ''}`}
                              onClick={() => handlePackageTimeBlockSelect(block)}
                            >
                              {block.start} - {block.end} Uhr
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className={classes.packageModalFooter}>
              <button
                type="button"
                className={`${classes.bookButtonModal} mediumLarge`}
                style={{
                  background: canStartPackageBooking ? 'black' : 'var(--light-grey-1)',
                  color: canStartPackageBooking ? 'white' : 'var(--grey-1)',
                  cursor: canStartPackageBooking ? 'pointer' : 'default',
                }}
                disabled={!canStartPackageBooking}
                onClick={handlePackageModalConfirm}
              >
                {packageModalButtonLabel}
              </button>
            </div>
          </div>
        </SmallModal>
      )}
      <div className={classes.container}>
        <div className={classes.header}>
          <div className={classes.headerPriceContainer}>
            {isPackagePricing ? (
              <>
                <h3 className="titleSemibold">
                  {displayPriceLabel}
                </h3>
                <h4>gesamt</h4>
              </>
            ) : (
              <>
                <h3 className="titleSemibold">{getCurrentHourlyPrice()} €</h3>
                <h4>pro Stunde</h4>
              </>
            )}
          </div>
          {!isPackagePricing && (
            <span style={{color: "var(--grey-1)"}} className="regularXxs">Mind. {minHours} Stunden</span>
          )}
        </div>
        <div className={classes.horizontalLine}></div>
        {isPackagePricing && (
          <>
            <div className={classes.labelsContainer}>
              <h3 className="semiboldMiddle">Paket auswählen</h3>
              <div className={classes.dateLabelContainer}>
                <div className={classes.customDropdownContainer} ref={packageDropdownMainRef}>
                  <div
                    className={`${classes.packageLabelContainer} regularLarge`}
                    onClick={togglePackageDropdownMain}
                  >
                    <div className={classes.packageContent}>
                    <span>
                      {(() => {
                        const pkg = getSelectedPackage();
                        if (!pkg) return 'Paket auswählen';
                        return `${pkg.durationHours} ${pkg.durationHours === 1 ? 'Stunde' : 'Stunden'}`;
                      })()}
                    </span>
                    <div className={classes.packagePriceBox}>
                      {getSelectedPackageBasePrice()} €
                    </div>
                    </div>

                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="8" viewBox="0 0 15 8" fill="none">
                      <path d="M1 0.86149L7.59865 6.8457M6.85146 6.82992L14 0.845703" stroke="#1F1F1F"/>
                    </svg>
                  </div>
                  {showPackageDropdownMain && (
                    <div className={classes.customDropdownOptions}>
                      {normalizedPackages.map((pkg) => (
                        <div
                          key={pkg.id}
                          className={`${classes.customDropdownOption} regularLarge`}
                          onClick={() => handlePackageOptionSelect(pkg.id, 'main')}
                        >
                          <span>
                            {pkg.durationHours} {pkg.durationHours === 1 ? 'Stunde' : 'Stunden'}
                          </span>
                          <span style={{ marginLeft: '8px' }}>
                            {(() => {
                              // Get base price directly from package (lowest price entry, no people group calculation)
                              const basePrice = getPackageBasePrice(pkg);
                              return basePrice > 0 ? `${basePrice} €` : '';
                            })()}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className={classes.horizontalLine}></div>
          </>
        )}

        {!isPackagePricing && (
          <div className={classes.labelsContainer}>
            <div onClick={toggleDatePicker} className={classes.dateLabelContainer}>
              <label className="regularLarge" htmlFor="date">Datum auswählen:</label>
                {showDatePicker ? (
                  <div className={classes.datePickerOverlay} ref={datePickerRef} onClick={handleOverlayClick}>
                    <DateChooser
                      fromDesktop={true}
                      nextSlide={handleDatePickerContinue}
                      bookingStudioData={bookingStudioData}
                    />
                  </div>
                ) : (
                  <div
                    className={`${classes.select} ${classes.datePicker} regularLarge`}
                    onClick={toggleDatePicker}
                  >
                    {bookingData.date ? format(parseLocalDateString(bookingData.date), 'dd.MM.yyyy', { locale: de }) : 'Datum wählen'}
                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="8" viewBox="0 0 15 8" fill="none">
                      <path d="M1 0.86149L7.59865 6.8457M6.85146 6.82992L14 0.845703" stroke="#1F1F1F"/>
                    </svg>
                  </div>
                )}
            </div>
            <div className={`${classes.timeOptionContainer} ${timeValidationError ? classes.errorBorder : ''} regularLarge`}>
              <div className={classes.labelContainer}>
                <select
                  className={`${classes.select} regularLarge ${classes.smaller}`}
                  id="start"
                  value={bookingData.startTime || startTime}
                  onChange={handleStartTimeChange}
                >
                  <option value="">Start</option>
                  {times.map((time, index) => (
                    <option key={index} value={time}>{time}</option>
                  ))}
                </select>
              </div>
              <div className={classes.verticalLine}></div>
              <div className={classes.labelContainer}>
                <select
                  className={`${classes.select} regularLarge ${classes.smaller}`}
                  id="end"
                  value={bookingData.endTime || endTime}
                  onChange={handleEndTimeChange}
                  disabled={!bookingData.startTime}
                >
                  <option value="">Ende</option>
                  {availableEndTimes.map((time, index) => (
                    <option key={index} value={time}>{time}</option>
                  ))}
                </select>
              </div>
            </div>
            {timeValidationError && (
              <div className={classes.errorMessage}>
                <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 11 11" fill="none">
                  <path fillRule="evenodd" clipRule="evenodd" d="M4.4452 5.5L0 1.0548L1.0548 0L5.5 4.4452L9.9452 0L11 1.0548L6.5548 5.5L11 9.9452L9.9452 11L5.5 6.5548L1.0548 11L0 9.9452L4.4452 5.5Z" fill="#CB1541"/>
                </svg>
                <span className="slimRegularSmallText">Start und Ende eingeben</span>
              </div>
            )}
          </div>
        )}

        {!isPackagePricing && (
          <div className={classes.horizontalLine}></div>
        )}

        {!isPackagePricing && (
          <div
            style={{
              marginBottom: isCurrentUserStudioOwner || !hasAuthenticatedUser ? "0px" : "20px",
            }}
            className={classes.containerPeople}
          >
            {useNumericPeopleInput ? (
              <div className={classes.customDropdownContainer}>
                <div className={`${classes.peopleLabelContainer} regularLarge`}>
                  <label htmlFor="peopleCountInput">
                    Personenanzahl
                  </label>
                  <input
                    id="peopleCountInput"
                    type="number"
                    className={`${classes.peopleNumberInput} regularLarge`}
                    min={1}
                    max={studioCapacity || undefined}
                    value={numericPeopleValue === '' ? '' : numericPeopleValue}
                    onChange={handlePeopleNumericChange}
                  />
                </div>
              </div>
            ) : (
              <div className={classes.customDropdownContainer} ref={peopleDropdownMainRef}>
                <div
                  className={`${classes.peopleLabelContainer} regularLarge`}
                  onClick={togglePeopleDropdownMain}
                >
                  <label>
                    Personenanzahl:{" "}
                    {(() => {
                      const current = bookingData?.numberOfPeople;
                      const options = generatePeopleOptions();
                      const match = options.find(o => o.value === current);
                      if (match) return match.label;
                      // Fallback if nothing selected yet
                      if (options.length > 0) return options[0].label;
                      return current || "1-5";
                    })()}
                  </label>
                  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="8" viewBox="0 0 15 8" fill="none">
                    <path d="M1 0.86149L7.59865 6.8457M6.85146 6.82992L14 0.845703" stroke="#1F1F1F"/>
                  </svg>
                </div>
                {showPeopleDropdownMain && (
                  <div className={classes.customDropdownOptions}>
                    {generatePeopleOptions().map((option) => (
                      <div
                        key={option?.value}
                        className={`${classes.customDropdownOption} regularLarge`}
                        onClick={() => handlePeopleOptionSelect(option?.value, 'main')}
                      >
                        {option?.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {isPackagePricing && hasPeopleDependentPackagePricing && (
          <div
            style={{
              marginBottom: isCurrentUserStudioOwner || !hasAuthenticatedUser ? "0px" : "20px",
            }}
            className={classes.containerPeople}
          >
            <h3 className="semiboldMiddle">Personenanzahl wählen</h3>
            <div className={classes.customDropdownContainer} ref={peopleDropdownMainRef}>
              <div
                className={`${classes.peopleLabelContainer} regularLarge`}
                onClick={togglePeopleDropdownMain}
              >
                <label>
                  {(() => {
                    const group = personGroups.find(g => g.key === selectedPeopleGroupKey) || personGroups[0];
                    if (!group) return "Auswählen";
                    const pkg = getSelectedPackage();
                    const extraLabel = getAdditionalCostLabelForGroup(pkg, group.key);
                    return `${group.min}-${group.max} Personen${extraLabel}`;
                  })()}
                </label>
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="8" viewBox="0 0 15 8" fill="none">
                  <path d="M1 0.86149L7.59865 6.8457M6.85146 6.82992L14 0.845703" stroke="#1F1F1F"/>
                </svg>
              </div>
              {showPeopleDropdownMain && (
                <div className={classes.customDropdownOptions}>
                  {personGroups.map((group) => (
                    <div
                      key={group.key}
                      className={`${classes.customDropdownOption} regularLarge`}
                      onClick={() => handlePeopleOptionSelect(group.key, 'main')}
                    >
                      {(() => {
                        const pkg = getSelectedPackage();
                        const extraLabel = getAdditionalCostLabelForGroup(pkg, group.key);
                        return `${group.min}-${group.max} Personen${extraLabel}`;
                      })()}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        {hasAuthenticatedUser &&
        <div
          style={{
            display: isCurrentUserStudioOwner ? "none" : "flex",
          }}
          className={classes.horizontalLine}
        ></div>
        }
        <div className={classes.bookingContainer} style={{ marginTop: isCurrentUserStudioOwner ? "20px" : "0px" }}>
          <button
            onClick={isPackagePricing ? openPackageBooking : startBooking}
            className={`${classes.bookButton} mediumLarge`}
            style={{
              display: isCurrentUserStudioOwner ? "none" : "flex",
            }}
          >
            {isPackagePricing ? "Termin finden" : "Buchen"}
          </button>
          {(!isPackagePricing && bookingData.startTime && bookingData.endTime) && (
              <div className={classes.priceContainer}>
              <h1 className="titleSemibold3">Preis</h1>
              <div className={classes.detailedContainer}>
                <p className="mediumLarge">{studioHourly} € x {duration} Stunden</p>
                <p className="mediumLarge">{bookingData.baseStudioPrice} €</p>
              </div>

              {/* Show discount if applied */}
              {!bookingData?.pricingUnavailable && bookingData?.appliedDiscount > 0 && (
                <div className={classes.detailedContainer}>
                  <p className="mediumLarge">{getDiscountLineLabel(bookingData)}</p>
                  <p style={{whiteSpace: 'nowrap'}} className="mediumLarge">-{bookingData.appliedDiscount} €</p>
                </div>
              )}

              {/* Show weekend surcharge if applied */}
              {!bookingData?.pricingUnavailable && bookingData?.appliedSurcharge > 0 && (
                <div className={classes.detailedContainer}>
                  <p className="mediumLarge">{getWeekendSurchargeLineLabel(bookingData)}</p>
                  <p style={{whiteSpace: 'nowrap'}} className="mediumLarge">+{bookingData.appliedSurcharge} €</p>
                </div>
              )}
              {/* Show fees if applied */}
              {!bookingData?.pricingUnavailable && bookingData?.appliedFees && bookingData.appliedFees.length > 0 && (
                bookingData.appliedFees.map((fee) => (
                  <div className={classes.detailedContainer} key={fee.id}>
                    <p className="mediumLarge">{fee.description || 'Gebühr'}</p>
                    <p style={{whiteSpace: 'nowrap'}} className="mediumLarge">+{fee.amount} €</p>
                  </div>
                ))
              )}
              <div className={classes.horizontalLine}></div>
              <div className={classes.detailedContainer}>
                <h1 className="titleSemibold3">Total</h1>
                {/* IMPORTANT: In this booking card we only show Studio + Gebühren,
                    never Creative-Packages or Equipment. */}
                <span className="titleSemibold3">{displayPriceLabel}</span>
              </div>
            </div>
          )}
          {isPackagePricing && (
            <div className={classes.priceContainer}>
              <h1 className="titleSemibold3">Preis</h1>
              <div className={classes.detailedContainer}>
                <p className="mediumLarge">
                  {(() => {
                    const pkg = getSelectedPackage();
                    if (!pkg) return 'Paket';
                    return `${pkg.durationHours} ${pkg.durationHours === 1 ? 'Stunde' : 'Stunden'}`;
                  })()}
                </p>
                <p className="mediumLarge">
                  {(() => {
                    if (bookingData?.pricingUnavailable) {
                      return 'Nicht verfügbar';
                    }
                    // Show the base package price (without surcharge/discount),
                    // mirroring the HOURLY block which uses baseStudioPrice.
                    // totalStudioPrice already includes the surcharge which is shown
                    // as a separate line below — using it here would double-count it.
                    const storePrice = bookingData?.baseStudioPrice;
                    if (typeof storePrice === 'number' && storePrice > 0) {
                      return storePrice;
                    }
                    // Fallback: compute from the selected package and people group.
                    return getPackageBasePriceForGroup(
                      getSelectedPackage(),
                      bookingData?.selectedPeopleGroupKey || selectedPeopleGroupKey
                    );
                  })()}{bookingData?.pricingUnavailable ? '' : ' €'}
                </p>
              </div>
              {bookingData?.pricingUnavailable && (
                <div className={classes.detailedContainer}>
                  <p className="mediumLarge">
                    {bookingData.pricingUnavailableReason || 'Preis für diese Personengruppe nicht verfügbar.'}
                  </p>
                </div>
              )}
              {/* Show discount if applied */}
              {!bookingData?.pricingUnavailable && bookingData?.appliedDiscount > 0 && (
                <div className={classes.detailedContainer}>
                  <p className="mediumLarge">{getDiscountLineLabel(bookingData)}</p>
                  <p className="mediumLarge">-{bookingData.appliedDiscount} €</p>
                </div>
              )}
              {/* Show weekend surcharge if applied */}
              {!bookingData?.pricingUnavailable && bookingData?.appliedSurcharge > 0 && (
                <div className={classes.detailedContainer}>
                  <p className="mediumLarge">{getWeekendSurchargeLineLabel(bookingData)}</p>
                  <p className="mediumLarge">+{bookingData.appliedSurcharge} €</p>
                </div>
              )}
              {/* Show fees if applied */}
              {!bookingData?.pricingUnavailable && bookingData?.appliedFees && bookingData.appliedFees.length > 0 && (
                bookingData.appliedFees.map((fee) => (
                  <div className={classes.detailedContainer} key={fee.id}>
                    <p className="mediumLarge">{fee.description || 'Gebühr'}</p>
                    <p className="mediumLarge">+{fee.amount} €</p>
                  </div>
                ))
              )}
              <div className={classes.horizontalLine}></div>
              <div className={classes.detailedContainer}>
                <h1 className="titleSemibold3">Total</h1>
                <span className="titleSemibold3">{displayPriceLabel}</span>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* <div className={classes.creativeBookingContainer}>
        <h3 className="regularXl">{studioCreativesData[0]?.creative?.jobTitle} dazubuchen</h3>
        <div className={classes.userContainer}>
          <div className={classes.userProfile}>
            <div className={classes.userPicture}>
              <img src={studioCreativesData[0]?.creative?.processedProfilePicture} alt="member" />
            </div>
            <div className={classes.userTextContainer}>
              <h3 className="mediumLarge">{studioCreativesData[0]?.creative?.displayName}</h3>
              <p className="regularXs" style={{color: "#696F76"}}>{studioCreativesData[0]?.creative?.jobTitle}</p>
            </div>
          </div>
          <div className={classes.switchContainer}>
            <label className={classes.switch}>
              <input checked={addFreelancer} onChange={handleAddFreelancerChange} type="checkbox" />
              <span className={classes.slider}></span>
            </label>
          </div>
        </div>
        <div className={classes.priceContainer}>
          <p className="titleSemibold3">{lowestPackagePrice} € <span className="regularMiddle">pro Stunde</span></p>
        </div>
      </div> */}
    </div>
  );
};

export default Booking;
